/**
 * @typedef {import('../../shared/PropertyService/Valuation').default} Valuation
 * @typedef {import('../../shared/PropertyService/PropertyService').default} PropertyService
 * @typedef {import('./PropertyValuationsComponent').Props} Props
 * @typedef {import('./PropertyValuationsComponent').State} State
 */
import { withProperty } from '../PropertyProvider';
import { withPropertyService } from 'service-container';
import Loading from '../../../shared/Loading';
import React from 'react';
import PropertyValuationSummary from './PropertyValuationSummary';
import captureError from '../../../utils/captureError';
import { translate } from '../../Internationalization';
import NoContent from '../../../shared/NoContent';

/** @extends {React.Component<Props, State>} */
class PropertyValuations extends React.Component {
	/** @type {string | null} */
	#activeValuationPropertyId = null;

	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);
		/**
		 * @type {State}
		 */
		this.state = {
			isGettingData: true,
			isRequestingAccurateValuation: false,
			requestStatus: null,
			valuations: null,
		};
	}

	async componentDidMount() {
		await this.#setValuations(this.#getPropertyId());
	}

	/**
	 * @param {Props} prevProps
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps) {
		const propertyId = this.#getPropertyId();

		if (propertyId !== PropertyValuations.getPropertyId(prevProps)) {
			await this.#setValuations(propertyId);
		}
	}

	/**
	 * @param {Props} props
	 * @returns {string | undefined}
	 */
	static getPropertyId(props) {
		return (
			props.match?.params?.propertyId ??
			props.property?.selectedProperty?.id
		);
	}

	/** @returns {string | undefined} */
	#getPropertyId() {
		return PropertyValuations.getPropertyId(this.props);
	}

	/**
	 * @param {string | undefined} propertyId
	 * @returns {Promise<void>}
	 */
	async #setValuations(propertyId) {
		this.#activeValuationPropertyId = propertyId ?? null;

		if (!propertyId) {
			this.setState({
				isGettingData: false,
				requestStatus: null,
				valuations: null,
			});
			return;
		}

		this.setState({
			isGettingData: true,
			requestStatus: null,
			valuations: null,
		});

		try {
			const valuations = await this.props.propertyService.getValuations(
				propertyId
			);

			if (this.#activeValuationPropertyId !== propertyId) {
				return;
			}

			this.setState({
				isGettingData: false,
				requestStatus: null,
				valuations,
			});
		} catch (error) {
			captureError(error);

			if (this.#activeValuationPropertyId !== propertyId) {
				return;
			}

			this.setState({
				isGettingData: false,
				requestStatus: null,
				valuations: null,
			});
		}
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #requestAccurateValuation() {
		this.setState({
			isRequestingAccurateValuation: true,
			requestStatus: null,
		});

		try {
			await this.props.propertyService.requestAccurateValuation();

			this.setState({
				isRequestingAccurateValuation: false,
				requestStatus: 'success',
			});
		} catch (error) {
			captureError(error);

			this.setState({
				isRequestingAccurateValuation: false,
				requestStatus: 'error',
			});
		}
	}

	/**
	 * @returns {boolean}
	 */
	#hasValuations() {
		if (this.state.valuations === null) {
			return false;
		}

		if (this.state.valuations.size < 1) {
			return false;
		}

		for (const [, valuationList] of this.state.valuations) {
			if (valuationList.length > 0) {
				return true;
			}
		}

		return false;
	}

	/**
	 * @returns {JSX.Element |null}
	 */
	render() {
		if (this.state.isGettingData) {
			return <Loading />;
		}

		const header = (
			<h2 className="h-ms-4 mb-6">
				{translate('homeowner.pages.finances.view.estimate.title')}
			</h2>
		);

		if (!this.#hasValuations()) {
			return (
				<section>
					{header}
					<NoContent />
				</section>
			);
		}

		if (this.state.valuations === null) {
			return (
				<section>
					{header}
					<NoContent />
				</section>
			);
		}

		return (
			<section className="mt-4">
				{header}
				<PropertyValuationSummary
					isRequestingAccurateValuation={
						this.state.isRequestingAccurateValuation
					}
					onRequestAccurateValuation={() =>
						this.#requestAccurateValuation()
					}
					requestStatus={this.state.requestStatus}
					showRequestAccurateValuation={true}
					valuations={this.state.valuations}
				/>
			</section>
		);
	}
}

export default withPropertyService(withProperty(PropertyValuations));
